// The constellation: one WebGL point cloud that morphs between shapes, one shape per
// chapter. three.js is loaded lazily, in its own chunk, once the browser is idle.
import type { WebGLRenderer as Renderer } from 'three'
import { G, fine, grid, isMobile } from './misc'

export type ShapeName = 'logo' | 'doc' | 'grid' | 'map' | 'growth' | 'check' | 'orb' | 'galaxy'

/** A chapter's `data-gl` payload: shape plus desktop (x, y, sc, o) and mobile (mx, my, ms, mo) framing. */
export interface GlParams {
  s: ShapeName
  x?: number
  y?: number
  o?: number
  sc?: number
  mx?: number
  my?: number
  mo?: number
  ms?: number
}

export interface Constellation {
  N: number
  go(p: GlParams): void
  setMapCount(n: number): void
  setHeroSpin(p: number): void
  warp(): void
  pulse(x: number, y: number): void
}

type RGB = [number, number, number]
type Pt = [number, number]
type Sample = [number, number, number, RGB]

/** Point count the cloud will use on this viewport (known before three.js loads). */
export const pointCount = (): number => (isMobile() ? 6500 : 15000)

const whenIdle = (): Promise<void> => new Promise((resolve) => {
  if ('requestIdleCallback' in window) requestIdleCallback(() => resolve(), { timeout: 2000 })
  else setTimeout(resolve, 1)
})

export async function createConstellation(canvas: HTMLCanvasElement): Promise<Constellation | null> {
  await whenIdle()
  // Destructured in the `then` so Rollup can tree-shake the three.js chunk.
  const lib = await import('three')
    .then(({ WebGLRenderer, Scene, PerspectiveCamera, BufferGeometry, BufferAttribute, ShaderMaterial, AdditiveBlending, Group, Points }) => (
      { WebGLRenderer, Scene, PerspectiveCamera, BufferGeometry, BufferAttribute, ShaderMaterial, AdditiveBlending, Group, Points }
    ))
    .catch(() => null)
  if (!lib) return null
  const { WebGLRenderer, Scene, PerspectiveCamera, BufferGeometry, BufferAttribute, ShaderMaterial, AdditiveBlending, Group, Points } = lib
  let renderer: Renderer
  try {
    renderer = new WebGLRenderer({ canvas, antialias: false, alpha: true, powerPreference: 'high-performance' })
  } catch {
    return null
  }
  const GRID = grid()
  const mobile = isMobile()
  const N = pointCount()
  renderer.setPixelRatio(Math.min(2, devicePixelRatio || 1))
  renderer.setClearColor(0x000000, 0)
  const scene = new Scene()
  const camera = new PerspectiveCamera(35, 1, 0.1, 100)
  camera.position.set(0, 0, 16)

  let seed = 20261030
  const rnd = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646 }
  const gauss = () => { let u = 0; while (!u) u = rnd(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(6.2831853 * rnd()) }
  const C: Record<string, RGB> = {
    sky: [0.10, 0.72, 0.99], celeste: [0.22, 0.74, 0.97], royal: [0.10, 0.34, 0.94], indigo: [0.43, 0.36, 0.96],
    light: [0.62, 0.80, 1.0], white: [1, 1, 1], green: [0.13, 0.86, 0.42], naval: [0.25, 0.42, 0.75],
  }
  const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
  const inPoly = (x: number, y: number, p: Pt[]) => {
    let c = false
    for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
      const [xi, yi] = p[i]; const [xj, yj] = p[j]
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c
    }
    return c
  }
  const bbox = (p: Pt[]) => ({ x0: Math.min(...p.map((q) => q[0])), x1: Math.max(...p.map((q) => q[0])), y0: Math.min(...p.map((q) => q[1])), y1: Math.max(...p.map((q) => q[1])) })
  const fill = (polys: Pt[][]): Pt => {
    const b = bbox(polys.flat())
    for (;;) {
      const x = b.x0 + rnd() * (b.x1 - b.x0); const y = b.y0 + rnd() * (b.y1 - b.y0)
      if (polys.some((p) => inPoly(x, y, p))) return [x, y]
    }
  }
  const edge = (p: Pt[]): Pt => {
    const segs = p.map((a, i): [Pt, Pt, number] => { const b = p[(i + 1) % p.length]; return [a, b, Math.hypot(b[0] - a[0], b[1] - a[1])] })
    let r = rnd() * segs.reduce((s, q) => s + q[2], 0)
    for (const [a, b, l] of segs) { if ((r -= l) <= 0) { const t = rnd(); return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t] } }
    return p[0]
  }
  interface Shape { p: Float32Array; c: Float32Array }
  const make = (fn: (i: number) => Sample): Shape => {
    const p = new Float32Array(N * 3); const c = new Float32Array(N * 3)
    for (let i = 0; i < N; i++) {
      const o = fn(i)
      p[i * 3] = o[0]; p[i * 3 + 1] = o[1]; p[i * 3 + 2] = o[2]
      c[i * 3] = o[3][0]; c[i * 3 + 1] = o[3][1]; c[i * 3 + 2] = o[3][2]
    }
    return { p, c }
  }

  // The AdminSE mark: the "A" and the check, in SVG units (363 x 364).
  const markPoly = (pts: Pt[], k: number, cx = 181.5, cy = 182): Pt[] => pts.map(([x, y]) => [(x - cx) * k, -(y - cy) * k])
  const MA: Pt[] = [[0, 309], [168, 0], [337, 309], [254, 309], [168, 152], [83, 309]]
  const MV: Pt[] = [[24, 138], [120, 138], [168, 212], [267, 56], [363, 56], [168, 364]]
  const LA = markPoly(MA, 7.4 / 364)
  const LV = markPoly(MV, 7.4 / 364)
  const logo = make(() => {
    const r = rnd()
    if (r < 0.36) { const [x, y] = fill([LA]); return [x, y, gauss() * 0.25, mix(C.royal, C.light, rnd() * 0.45)] }
    if (r < 0.47) { const [x, y] = edge(LA); return [x, y, gauss() * 0.05, C.light] }
    if (r < 0.85) { const [x, y] = fill([LV]); return [x, y, 0.4 + gauss() * 0.22, mix(C.sky, C.celeste, rnd())] }
    const [x, y] = edge(LV); return [x, y, 0.4 + gauss() * 0.05, C.white]
  })

  const DW = 4.8; const DH = 6.4
  const DOC: Pt[] = [[-DW / 2, DH / 2], [DW / 2, DH / 2], [DW / 2, -DH / 2], [-DW / 2, -DH / 2]]
  const DV = markPoly(MV, 1.5 / 364).map(([x, y]): Pt => [x + 1.45, y - 2.25])
  const lineLen = [0.9, 0.72, 0.84, 0.6, 0.92, 0.7, 0.8, 0.55, 0.88, 0.66]
  const doc = make(() => {
    const r = rnd()
    if (r < 0.2) { const [x, y] = edge(DOC); return [x, y, gauss() * 0.04, C.light] }
    if (r < 0.3) return [-DW / 2 + 0.35 + rnd() * (DW - 0.7), DH / 2 - 0.45 - rnd() * 0.55, gauss() * 0.05, C.indigo]
    if (r < 0.74) { const j = Math.floor(rnd() * lineLen.length); return [-DW / 2 + 0.4 + rnd() * (DW - 0.8) * lineLen[j], 1.75 - j * 0.42 + gauss() * 0.025, gauss() * 0.04, mix(C.light, C.white, rnd())] }
    if (r < 0.88) { const [x, y] = fill([DV]); return [x, y, 0.2 + gauss() * 0.06, C.sky] }
    return [-DW / 2 + rnd() * DW, -DH / 2 + rnd() * DH, gauss() * 0.3, mix(C.naval, C.light, rnd() * 0.3)]
  })

  const COLS = 8; const ROWS = 4; const BEST = 13
  const gridShape = make((i) => {
    const k = i % (COLS * ROWS)
    const cx = (k % COLS - (COLS - 1) / 2) * 0.98; const cy = ((ROWS - 1) / 2 - Math.floor(k / COLS)) * 0.98
    const a = rnd() * 6.2832; const ring = rnd() < 0.72; const rr = ring ? 0.31 + gauss() * 0.012 : Math.sqrt(rnd()) * 0.2
    const col = k % 3 === 0 ? C.indigo : (k % 3 === 1 ? C.celeste : C.light)
    return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, gauss() * 0.05, k === BEST ? C.green : mix(col, C.white, ring ? 0 : 0.4)]
  })

  // Argentina (with Tierra del Fuego and the Malvinas) and Uruguay, lon/lat. North is up.
  const AR: Pt[] = [[-67.18, -22.82], [-66.75, -22.4], [-66.29, -21.8], [-65.75, -22.1], [-65, -22.1], [-64.55, -22.25], [-64.32, -22.83], [-63.95, -22], [-62.85, -22.04], [-62.3, -22.6], [-61.5, -23.3], [-60.8, -23.8], [-60, -24.1], [-59.2, -24.55], [-58.4, -24.95], [-57.75, -25.15], [-57.85, -25.8], [-58.2, -26.6], [-58.6, -27.25], [-57.9, -27.35], [-56.9, -27.45], [-55.9, -27.3], [-55.2, -26.8], [-54.75, -26.1], [-54.6, -25.6], [-54.1, -25.55], [-53.85, -25.95], [-53.65, -26.25], [-53.8, -27.1], [-54.5, -27.45], [-55.3, -27.9], [-56, -28.5], [-56.75, -29.1], [-57.3, -29.75], [-57.62, -30.18], [-57.85, -30.9], [-57.9, -31.6], [-58.1, -32.3], [-58.2, -33], [-58.4, -33.55], [-58.45, -34], [-58.4, -34.55], [-57.95, -34.85], [-57.3, -35.25], [-57.15, -35.8], [-56.72, -36.32], [-56.68, -36.9], [-57.1, -37.55], [-57.55, -38.05], [-58.7, -38.55], [-59.8, -38.85], [-61.2, -38.98], [-62.25, -38.8], [-62.1, -39.5], [-62.35, -40.2], [-62.2, -40.7], [-63, -41.1], [-64.2, -40.9], [-65, -40.75], [-65.15, -41.3], [-64.6, -42], [-63.6, -42.6], [-64.3, -42.95], [-64.9, -42.7], [-65.05, -43.3], [-65.3, -44.2], [-65.6, -45], [-66.8, -45.25], [-67.5, -45.9], [-67.4, -46.5], [-66.4, -47.1], [-65.8, -47.75], [-67.1, -48.7], [-67.7, -49.3], [-68.3, -50.1], [-69.1, -50.8], [-69.1, -51.6], [-68.4, -52.3], [-69.6, -52.35], [-71, -52.1], [-72, -51.95], [-72.4, -51.2], [-73.2, -50.5], [-73.3, -49.5], [-72.6, -48.6], [-72.4, -47.8], [-71.9, -46.8], [-71.8, -45.9], [-71.5, -45], [-71.8, -44.2], [-71.7, -43.3], [-71.8, -42.2], [-71.8, -41], [-71.6, -40], [-71.2, -39.3], [-70.9, -38.5], [-71.1, -37.6], [-70.5, -36.4], [-70.4, -35.3], [-70, -34.2], [-70.1, -33.2], [-69.9, -32.4], [-70.3, -31.3], [-69.8, -30.2], [-69.6, -29.1], [-68.9, -28.1], [-68.4, -27.1], [-68.5, -26.2], [-68.4, -25.1], [-67.4, -24.3], [-67, -23.5]]
  const TDF: Pt[] = [[-68.6, -52.65], [-68.25, -53.1], [-67.75, -53.8], [-66.95, -54.25], [-65.8, -54.65], [-65.1, -54.7], [-65.5, -55], [-66.7, -54.95], [-67.8, -54.88], [-68.3, -54.85], [-68.62, -54.9], [-68.62, -52.65]]
  const MAL1: Pt[] = [[-59.6, -51.4], [-58.9, -51.3], [-58, -51.5], [-57.7, -51.75], [-58.2, -52.05], [-59, -52.25], [-59.6, -51.95]]
  const MAL2: Pt[] = [[-61.3, -51.5], [-60.4, -51.35], [-59.9, -51.6], [-60, -52.1], [-60.6, -52.25], [-61.2, -51.95]]
  const UY: Pt[] = [[-57.62, -30.18], [-56.8, -30.1], [-56, -30.8], [-55.6, -30.85], [-54.6, -31.5], [-53.9, -31.9], [-53.2, -32.6], [-53.4, -33.3], [-53.4, -33.75], [-54.1, -34.4], [-54.95, -34.95], [-55.8, -34.8], [-56.2, -34.9], [-57, -34.6], [-57.85, -34.45], [-58.4, -33.95], [-58.35, -33.05], [-58.1, -32.3], [-57.9, -31.6], [-57.85, -30.9]]
  const KX = Math.cos((-38 * Math.PI) / 180)
  const MS = 8.8 / 33.4
  // WebGL's y axis points up, so latitude maps straight to y (north at the top).
  const pj = ([lo, la]: Pt): Pt => [(lo + 63.4) * KX * MS, (la + 38.4) * MS]
  const MAP = [AR, TDF, MAL1, MAL2, UY].map((p) => p.map(pj))
  const CITIES: [number, number, number, number][] = [[-58.45, -34.62, 0.3, 0.35], [-58.75, -34.75, 0.08, 0.4], [-57.95, -34.92, 0.03, 0.15], [-64.18, -31.42, 0.07, 0.25], [-60.65, -32.95, 0.06, 0.2], [-68.83, -32.89, 0.045, 0.2], [-65.22, -26.82, 0.035, 0.18], [-57.56, -38, 0.025, 0.12], [-65.41, -24.79, 0.022, 0.15], [-60.7, -31.63, 0.02, 0.15], [-68.53, -31.54, 0.016, 0.12], [-68.06, -38.95, 0.016, 0.15], [-62.27, -38.72, 0.013, 0.12], [-58.83, -27.47, 0.012, 0.1], [-58.98, -27.45, 0.012, 0.1], [-55.9, -27.37, 0.012, 0.12], [-60.52, -31.73, 0.011, 0.1], [-64.26, -27.8, 0.011, 0.12], [-65.3, -24.19, 0.009, 0.1], [-64.35, -33.12, 0.008, 0.1], [-67.5, -45.86, 0.008, 0.12], [-71.3, -41.13, 0.007, 0.12], [-65.3, -43.25, 0.005, 0.1], [-69.22, -51.62, 0.004, 0.1], [-68.3, -54.8, 0.003, 0.06], [-64.29, -36.62, 0.006, 0.1], [-66.34, -33.3, 0.006, 0.1], [-56.17, -34.88, 0.05, 0.15], [-54.95, -34.93, 0.008, 0.08], [-57.96, -31.38, 0.005, 0.08]]
  const wsum = CITIES.reduce((s, c) => s + c[2], 0) + 0.26
  const map = make(() => {
    for (;;) {
      let pick = rnd() * wsum
      const city = CITIES.find((c) => (pick -= c[2]) < 0)
      let x: number; let y: number
      if (city) { [x, y] = pj([city[0] + gauss() * city[3] * 1.15, city[1] + gauss() * city[3]]) } else { [x, y] = fill(MAP) }
      if (MAP.some((p) => inPoly(x, y, p))) { const r = rnd(); return [x, y, gauss() * 0.06, r < 0.55 ? C.sky : r < 0.85 ? C.light : C.white] }
    }
  })

  const growth = make(() => {
    const r = rnd()
    const bars = 12; const bw = 0.36; const gap = 0.16; const x0 = -((bars * bw + (bars - 1) * gap) / 2)
    const hOf = (k: number) => 0.6 + 5.6 * Math.pow(k / (bars - 1), 1.7)
    if (r < 0.74) { const k = Math.floor(rnd() * bars); return [x0 + k * (bw + gap) + rnd() * bw, -3 + rnd() * hOf(k), gauss() * 0.05, mix(C.indigo, C.celeste, k / (bars - 1))] }
    const kf = rnd() * (bars - 1)
    return [x0 + kf * (bw + gap) + bw / 2, -3 + hOf(kf) + 0.35 + gauss() * 0.03, gauss() * 0.04, C.white]
  })

  const BC = markPoly(MV, 7.6 / 364)
  const check = make(() => {
    const r = rnd()
    if (r < 0.7) { const [x, y] = fill([BC]); return [x, y, gauss() * 0.3, mix(C.sky, C.white, Math.max(0, (y + 3.8) / 7.6) * 0.6)] }
    if (r < 0.9) { const [x, y] = edge(BC); return [x, y, gauss() * 0.04, C.white] }
    const a = rnd() * 6.2832; const rr = 4.4 + rnd() * 3; return [Math.cos(a) * rr, Math.sin(a) * rr * 0.7, gauss() * 1.2, mix(C.indigo, C.celeste, rnd())]
  })

  // The copilot's core: a sphere with a tilted ring and a bright center.
  const TILT = 0.38
  const orb = make(() => {
    const r = rnd()
    if (r < 0.6) {
      const u = rnd() * 2 - 1; const th = rnd() * 6.2832; const s = Math.sqrt(1 - u * u); const R = 3 + gauss() * 0.05
      return [s * Math.cos(th) * R, u * R, s * Math.sin(th) * R, mix(C.indigo, C.celeste, (u + 1) / 2)]
    }
    if (r < 0.82) {
      const th = rnd() * 6.2832; const R = 4.3 + gauss() * 0.07
      const rx = Math.cos(th) * R; const rz = Math.sin(th) * R
      return [rx, -rz * Math.sin(TILT) + gauss() * 0.03, rz * Math.cos(TILT), mix(C.celeste, C.white, rnd() * 0.6)]
    }
    const R = Math.pow(rnd(), 0.6) * 1.5; const u = rnd() * 2 - 1; const th = rnd() * 6.2832; const s = Math.sqrt(1 - u * u)
    return [s * Math.cos(th) * R, u * R, s * Math.sin(th) * R, mix(C.white, C.indigo, rnd() * 0.7)]
  })

  const galaxy = make(() => {
    const arm = Math.floor(rnd() * 3); const rr = Math.pow(rnd(), 0.7) * 10
    const a = (arm * 6.2832) / 3 + rr * 0.42 + gauss() * 0.28; const t = Math.min(1, rr / 10)
    return [Math.cos(a) * rr, Math.sin(a) * rr * 0.55, gauss() * 0.9, mix(mix(C.white, C.celeste, Math.min(1, t * 2)), C.indigo, Math.max(0, t * 1.6 - 0.6))]
  })

  const SHAPES: Record<ShapeName, Shape> = { logo, doc, grid: gridShape, map, growth, check, orb, galaxy }

  const geo = new BufferGeometry()
  const pos = new Float32Array(N * 3); const col = new Float32Array(N * 3); const siz = new Float32Array(N); const alp = new Float32Array(N)
  const base = new Float32Array(N * 3); const from = new Float32Array(N * 3); const fromC = new Float32Array(N * 3)
  const disp = new Float32Array(N * 3); const dir = new Float32Array(N * 3); const delay = new Float32Array(N); const ph = new Float32Array(N); const alpT = new Float32Array(N); const zd = new Float32Array(N)
  for (let i = 0; i < N; i++) {
    zd[i] = Math.pow(rnd(), 0.6)
    siz[i] = 0.55 + Math.pow(rnd(), 3) * 1.6
    delay[i] = rnd(); ph[i] = rnd() * 6.2832; alp[i] = 1; alpT[i] = 1
    const a = rnd() * 6.2832; const b = Math.acos(2 * rnd() - 1); const m = 0.6 + rnd() * 1.8
    dir[i * 3] = Math.sin(b) * Math.cos(a) * m; dir[i * 3 + 1] = Math.sin(b) * Math.sin(a) * m; dir[i * 3 + 2] = Math.cos(b) * m
  }
  let to = galaxy
  base.set(galaxy.p); pos.set(galaxy.p); col.set(galaxy.c)
  const posAttr = new BufferAttribute(pos, 3); const colAttr = new BufferAttribute(col, 3); const alpAttr = new BufferAttribute(alp, 1)
  geo.setAttribute('position', posAttr)
  geo.setAttribute('aColor', colAttr)
  geo.setAttribute('aSize', new BufferAttribute(siz, 1))
  geo.setAttribute('aAlpha', alpAttr)
  const mat = new ShaderMaterial({
    uniforms: { uPR: { value: renderer.getPixelRatio() }, uSize: { value: mobile ? 5.2 : 6.2 }, uOpacity: { value: 0 } },
    vertexShader: `attribute vec3 aColor; attribute float aSize; attribute float aAlpha;
      uniform float uPR; uniform float uSize; varying vec3 vC; varying float vA;
      void main(){ vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_Position = projectionMatrix * mv;
        gl_PointSize = uSize * aSize * uPR * (10.0 / -mv.z); vC = aColor; vA = aAlpha; }`,
    fragmentShader: `uniform float uOpacity; varying vec3 vC; varying float vA;
      void main(){ float d = length(gl_PointCoord - 0.5); if (d > 0.5) discard; float a = smoothstep(0.5, 0.0, d); a *= a;
        gl_FragColor = vec4(vC, a * vA * uOpacity); }`,
    transparent: true, depthWrite: false, blending: AdditiveBlending,
  })
  const group = new Group()
  group.add(new Points(geo, mat))
  scene.add(group)

  const view = { x: 0, y: 0, s: 1, o: 0, r: 1, ps: 1 }
  const st = { p: 1 }
  let morphing = false; let alphaDirty = false; let mapCount = N
  let heroSpin = 0; let orbSpin = 0; let kick = 0
  let shock: { nx: number; ny: number; t0: number } | null = null
  const mouse = { x: 0, y: 0, on: false }
  if (fine()) {
    addEventListener('pointermove', (e) => { mouse.x = (e.clientX / innerWidth) * 2 - 1; mouse.y = -((e.clientY / innerHeight) * 2 - 1); mouse.on = true })
    document.addEventListener('pointerleave', () => { mouse.on = false })
  }
  // A click sends a shockwave through the cloud.
  addEventListener('pointerdown', (e) => { shock = { nx: (e.clientX / innerWidth) * 2 - 1, ny: -((e.clientY / innerHeight) * 2 - 1), t0: performance.now() } })

  const resize = () => { renderer.setSize(innerWidth, innerHeight, false); camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix() }
  resize()
  addEventListener('resize', resize)

  const applyAlpha = () => { for (let i = 0; i < N; i++) alpT[i] = to === map && i >= mapCount ? 0 : 1; alphaDirty = true }
  const morph = (name: ShapeName) => {
    const next = SHAPES[name]
    if (!next || next === to) return
    from.set(base); fromC.set(col)
    to = next; st.p = 0; morphing = true
    applyAlpha()
    G.killTweensOf(st)
    G.to(st, { p: 1, duration: 2.4, ease: 'none', onComplete: () => { morphing = false; base.set(to.p); col.set(to.c); colAttr.needsUpdate = true } })
  }

  const t0 = performance.now(); let raf = 0; let last = 0
  const frame = (now: number) => {
    raf = requestAnimationFrame(frame)
    const t = (now - t0) / 1000
    const dt = Math.min(0.05, (now - (last || now)) / 1000); last = now
    if (morphing) {
      const p = st.p; const tp = to.p; const tc = to.c
      for (let i = 0; i < N; i++) {
        let k = p * 1.75 - delay[i] * 0.75; k = k < 0 ? 0 : k > 1 ? 1 : k
        const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2
        const s = Math.sin(k * Math.PI); const i3 = i * 3
        base[i3] = from[i3] + (tp[i3] - from[i3]) * e + dir[i3] * s
        base[i3 + 1] = from[i3 + 1] + (tp[i3 + 1] - from[i3 + 1]) * e + dir[i3 + 1] * s
        base[i3 + 2] = from[i3 + 2] + (tp[i3 + 2] - from[i3 + 2]) * e + dir[i3 + 2] * s
        col[i3] = fromC[i3] + (tc[i3] - fromC[i3]) * e
        col[i3 + 1] = fromC[i3 + 1] + (tc[i3 + 1] - fromC[i3 + 1]) * e
        col[i3 + 2] = fromC[i3 + 2] + (tc[i3 + 2] - fromC[i3 + 2]) * e
      }
      colAttr.needsUpdate = true
    }
    const h = 2 * Math.tan((camera.fov * Math.PI) / 360) * camera.position.z
    const w = h * camera.aspect
    orbSpin = to === orb ? orbSpin + dt * 0.35 : orbSpin * 0.96
    const hs = Math.sin(heroSpin * Math.PI)
    // Leaving the hero, the cloud rushes past the camera like a jump to hyperspace.
    kick = Math.max(0, kick - dt * 1.2)
    const warp = Math.max(Math.pow(Math.sin(heroSpin * Math.PI), 2) * (heroSpin > 0.5 ? 1 : 0.35), kick)
    group.position.x = view.x * w * Math.min(1, GRID / innerWidth)
    group.position.y = view.y * h
    const sc = view.s * (1 + hs * 0.28)
    group.scale.setScalar(sc)
    group.rotation.y = (Math.sin(t * 0.13) * 0.24 + (mouse.on ? mouse.x * 0.2 : 0)) * view.r + orbSpin + hs * 1.15
    group.rotation.x = (Math.sin(t * 0.09) * 0.06 - (mouse.on ? mouse.y * 0.12 : 0)) * view.r
    mat.uniforms.uOpacity.value = view.o
    mat.uniforms.uSize.value = (mobile ? 5.2 : 6.2) * view.ps

    const lx = mouse.on ? (mouse.x * w * 0.5 - group.position.x) / sc : 1e4
    const ly = mouse.on ? (mouse.y * h * 0.5 - group.position.y) / sc : 1e4
    let sx = 1e4; let sy = 1e4; let srad = 0; let sk = 0
    if (shock) {
      const age = (now - shock.t0) / 1000
      if (age > 1.5) shock = null
      else { sx = (shock.nx * w * 0.5 - group.position.x) / sc; sy = (shock.ny * h * 0.5 - group.position.y) / sc; srad = age * 9; sk = (1 - age / 1.5) * 0.4 }
    }
    const R = 1.25; const R2 = R * R
    for (let i = 0; i < N; i++) {
      const i3 = i * 3; const bx = base[i3]; const by = base[i3 + 1]
      const dx = bx - lx; const dy = by - ly; const d2 = dx * dx + dy * dy
      if (d2 < R2) { const d = Math.sqrt(d2) + 1e-4; const f = (1 - d / R) * 0.16; disp[i3] += (dx / d) * f; disp[i3 + 1] += (dy / d) * f }
      if (sk) {
        const ex = bx - sx; const ey = by - sy; const de = Math.sqrt(ex * ex + ey * ey) + 1e-4; const band = Math.abs(de - srad)
        if (band < 0.9) { const f = (1 - band / 0.9) * sk; disp[i3] += (ex / de) * f; disp[i3 + 1] += (ey / de) * f }
      }
      disp[i3] *= 0.9; disp[i3 + 1] *= 0.9
      const wob = ph[i] + t * 0.7
      const spread = 1 + warp * zd[i] * 0.9
      pos[i3] = (bx + disp[i3] + Math.sin(wob) * 0.028) * spread
      pos[i3 + 1] = (by + disp[i3 + 1] + Math.cos(wob * 0.9) * 0.028) * spread
      pos[i3 + 2] = base[i3 + 2] + warp * zd[i] * 26
    }
    posAttr.needsUpdate = true
    if (alphaDirty) {
      let moving = false
      for (let i = 0; i < N; i++) { const d = alpT[i] - alp[i]; if (Math.abs(d) > 0.004) { alp[i] += d * 0.07; moving = true } else alp[i] = alpT[i] }
      alpAttr.needsUpdate = true
      alphaDirty = moving
    }
    renderer.render(scene, camera)
  }
  const start = () => { if (!raf) { last = 0; raf = requestAnimationFrame(frame) } }
  const pause = () => { cancelAnimationFrame(raf); raf = 0 }
  start()
  document.addEventListener('visibilitychange', () => (document.hidden ? pause() : start()))

  return {
    N,
    go(p) {
      morph(p.s)
      const m = isMobile()
      G.to(view, {
        x: m ? (p.mx ?? 0) : (p.x ?? 0), y: m ? (p.my ?? p.y ?? 0) : (p.y ?? 0),
        s: m ? (p.ms ?? 0.6) : (p.sc ?? 1), o: m ? (p.mo ?? p.o ?? 1) : (p.o ?? 1),
        r: p.s === 'map' ? 0.35 : 1, ps: p.s === 'map' && mapCount < N * 0.1 ? 1.9 : 1,
        duration: 1.9, ease: 'power3.inOut', overwrite: true,
      })
    },
    setMapCount(n) { mapCount = n; applyAlpha(); G.to(view, { ps: n < N * 0.1 ? 1.9 : n < N * 0.5 ? 1.3 : 1, duration: 1.2, ease: 'power2.out' }) },
    setHeroSpin(p) { heroSpin = p },
    warp() { kick = 1 },
    pulse(x, y) { shock = { nx: (x / innerWidth) * 2 - 1, ny: -((y / innerHeight) * 2 - 1), t0: performance.now() } },
  }
}
